const getLocale = () => window.navigator.language; // Get browser default language

const buildFormatter = (locale, currency, noDecimal) => {
  try {
    return new Intl.NumberFormat(locale, {
      style: noDecimal ? "decimal" : "currency",
      currency,
    });
  } catch (err) {
    // an unsupported locale or currency code — fall back to a plain number formatter
    return new Intl.NumberFormat(undefined, { style: "decimal" });
  }
};

const DEFAULT_SEPARATORS = { group: ",", decimal: ".", maxFractionDigits: 2 };

const separatorCache = {};

// Intl reports the separators it renders with, so the parser can read back exactly what
// the formatter wrote instead of guessing at them.
//
// 11111.11 is a deliberate sample. Spanish, Italian, Portuguese and Polish do not group
// four-digit numbers, so a smaller sample reports no group separator at all — which is
// what used to leave "20.000" being read as the number 20.
const getSeparators = (locale, currency) => {
  const cacheKey = `${locale}|${currency}`;
  if (separatorCache[cacheKey]) {
    return separatorCache[cacheKey];
  }
  let separators = DEFAULT_SEPARATORS;
  try {
    const formatter = buildFormatter(locale, currency);
    const parts = formatter.formatToParts(11111.11);
    separators = {
      group: parts.find((part) => part.type === "group")?.value || "",
      // A zero-decimal currency (VND, JPY) renders no decimal part at all, so the
      // decimal separator has to come from the plain number formatter, which always
      // has one.
      decimal:
        new Intl.NumberFormat(locale)
          .formatToParts(1.1)
          .find((part) => part.type === "decimal")?.value || ".",
      maxFractionDigits: formatter.resolvedOptions().maximumFractionDigits,
    };
  } catch (err) {
    separators = DEFAULT_SEPARATORS;
  }
  separatorCache[cacheKey] = separators;
  return separators;
};

// Digits, the two separators, whitespace and a sign. Anything else — a currency symbol,
// a letter — is not an amount, and has always been reported back as NaN so that
// Validation.number can flag it.
const AMOUNT_CHARACTERS = /^[+-]?[\d\s.,]*[+-]?$/;

// The shape the API and Number() both speak: a plain decimal with no grouping.
const MACHINE_NUMBER = /^\d+(\.\d+)?$/;

const WHITESPACE = /\s/g;

// A dot-grouping locale makes "20.000" ambiguous — it is both a grouped twenty thousand
// and a valid JS literal for twenty. Grouping is the right reading whenever the
// separators split runs of exactly three digits; anything else is a machine decimal.
const isGroupedLikeLocale = (body, group) => {
  if (!group) {
    return false;
  }
  const runs = body.split(group);
  if (runs.length < 2 || !/^\d{1,3}$/.test(runs[0])) {
    return false;
  }
  return runs.slice(1).every((run) => /^\d{3}$/.test(run));
};

const splitAmountString = (raw, { group, decimal, maxFractionDigits }) => {
  if (!AMOUNT_CHARACTERS.test(raw)) {
    return { wholeNumber: "", decimal: "", value: NaN };
  }
  const negative = raw.indexOf("-") >= 0;
  const sign = negative ? -1 : 1;
  const body = raw.replace(/[+-]/g, "").replace(WHITESPACE, "");

  const grouped = isGroupedLikeLocale(body, group);

  // A machine number is passed through untouched. Its precision belongs to whoever sent
  // it, so it is never regrouped and never truncated to the currency's decimals.
  if (!grouped && MACHINE_NUMBER.test(body)) {
    const [whole, fraction = ""] = body.split(".");
    return { wholeNumber: whole, decimal: fraction, value: Number(body) * sign };
  }

  const ungrouped = group ? body.split(group).join("") : body;
  const [wholePart = "", fractionPart = ""] = decimal ? ungrouped.split(decimal) : [ungrouped];
  const wholeDigits = wholePart.replace(/\D/g, "");
  const fractionDigits = fractionPart.replace(/\D/g, "").substring(0, maxFractionDigits);

  if (!wholeDigits && !fractionDigits) {
    // Separators alone have always read as 0, a lone sign as NaN. Callers depend on
    // both: Validation.number flags the NaN, and the price fields lean on the 0 while
    // a decimal separator is still the last character typed.
    return { wholeNumber: "0", decimal: "", value: negative ? NaN : 0 };
  }
  const digits = `${wholeDigits || "0"}${fractionDigits ? `.${fractionDigits}` : ""}`;
  return { wholeNumber: wholeDigits || "0", decimal: fractionDigits, value: Number(digits) * sign };
};

export const parseMoneyToNumber = (val = "") => {
  const locale = getLocale();
  const currency = getCurrency();
  const separators = getSeparators(locale, currency);
  const base = { grouper: separators.group, fraction: separators.decimal, val };

  // A real number needs no parsing at all. A string is never handed to Number()
  // directly, because in a dot-grouping locale "20.000" is a valid JS numeric literal
  // that evaluates to 20.
  if (typeof val === "number") {
    return {
      ...base,
      value: val,
      wholeNumber: `${Math.trunc(val)}`,
      decimal: `${val}`.split(".")[1] || "",
    };
  }

  const raw = `${val === null || val === undefined ? "" : val}`.trim();
  if (!raw) {
    return { ...base, value: 0, wholeNumber: "0", decimal: "" };
  }
  return { ...base, ...splitAmountString(raw, separators) };
};

export const formatNumberToMoney = (value = "", roundUp = true, noDecimal) => {
  const locale = getLocale();
  const currency = getCurrency();
  let defaultZeroValue = "0.00";

  if (noDecimal) {
    defaultZeroValue = "0";
  }

  if (!currency) {
    return value || defaultZeroValue;
  }

  const parsedNumber = parseMoneyToNumber(value);

  // negative amounts have problems with converting to currency.
  // format the absolute value, then append the negative sign when returning
  if (!Number.isFinite(parsedNumber.value)) {
    return "0";
  }
  const negativeAmount = parsedNumber.value < 0;
  const target = roundUp ? parsedNumber.value : Math.trunc(parsedNumber.value);

  let amount = buildFormatter(locale, currency, noDecimal)
    .format(Math.abs(target))
    .replace(/[^0-9.,-\s]/g, "");

  if (!roundUp && parsedNumber.decimal) {
    amount = `${amount.split(parsedNumber.fraction)[0]}${parsedNumber.fraction}${
      parsedNumber.decimal
    }`;
  }

  const formattedAmount = amount.toString().trim();
  if (!negativeAmount) {
    return formattedAmount;
  }
  return `-${formattedAmount.replaceAll(WHITESPACE, "").replaceAll("-", "")}`;
};

export const formatNumberToMoneyCurrency = (value, roundUp = true) => {
  const formattedAmount = formatNumberToMoney(value, roundUp, false);
  // const currency = getCurrencySymbol();
  return `${formattedAmount}`.trim();
};

export const formatNumberToMoneyWithCurrencySymbol = (value, roundUp = true) => {
  const formattedAmount = formatNumberToMoney(value, roundUp, false) || "0";
  const currency = getCurrencySymbol();
  return `${currency} ${formattedAmount}`.trim();
};

export const formatNumberToMoneyWithCurrencyParams = (value, roundUp = true, currency) => {
  const formattedAmount = formatNumberToMoney(value, roundUp, false);
  return `${currency} ${formattedAmount}`.trim();
};

export const getCurrency = () => {
  return localStorage.getItem("currencyCode");
};

export const getCurrencySymbol = () => {
  return localStorage.getItem("currencySymbol");
};
